import type { ErrorInfo, FC, PropsWithChildren, ReactNode } from "react";
import { Component } from "react";
import { useTranslation } from "react-i18next";

interface DefaultErrorFallbackProps {
    onReset: () => void;
}
const DefaultErrorFallback: FC<DefaultErrorFallbackProps> = ({ onReset }) => {
    const { t } = useTranslation();
    return (
        <div data-testid="default-error-fallback">
            <span>{t("error.somethingWentWrong")}</span>
            <button type="button" className="link" onClick={onReset}>
                {t("error.tryAgain")}
            </button>
        </div>
    );
};

interface ErrorBoundaryState {
    error: unknown | null;
}

interface ErrorBoundaryProps extends PropsWithChildren {
    fallback?: ReactNode;
    onError?: (error: unknown, errorInfo: ErrorInfo) => void;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
        return { error };
    }

    override componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
        this.props.onError?.(error, errorInfo);
    }

    handleReset = () => {
        this.setState({ error: null });
    };

    override render() {
        if (this.state.error) {
            return typeof this.props.fallback === "undefined" ? (
                <DefaultErrorFallback onReset={this.handleReset} />
            ) : (
                this.props.fallback
            );
        }

        return this.props.children;
    }
}
